import React, { useState, useEffect, Fragment, useRef } from 'react';
import EmailCard from './EmailCards';
import { generateAIResponse } from '../utils/aiGenerator';
import { canUserGenerate, incrementGenerationCount } from '../utils/firebaseSettings';
import Toast from './Toast';
import { checkCustomerDetails, cancelStripeSubscription, createStripeRefund } from '../utils/stripeApi';

const Board = ({ emails, setEmails, accessToken, knowledgebase, userSettings, subscriptionPlan, userInfo, currentPeriodStart, currentPeriodEnd }) => {
  const [selectedThread, setSelectedThread] = useState(null);
  const [inProgressEmails, setInProgressEmails] = useState([]);
  const [doneEmails, setDoneEmails] = useState([]);
  const [draggedEmail, setDraggedEmail] = useState(null);
  const [draggedFromColumn, setDraggedFromColumn] = useState(null);
  const [dropTarget, setDropTarget] = useState({ column: null, index: null });
  const [isDragging, setIsDragging] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [lastSentThread, setLastSentThread] = useState(null);
  const [canGenerate, setCanGenerate] = useState(true);
  const [generationRemaining, setGenerationRemaining] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelSubscriptionId, setCancelSubscriptionId] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundPayment, setRefundPayment] = useState(null);
  const [refundAmountType, setRefundAmountType] = useState('full');
  const [refundPartialAmount, setRefundPartialAmount] = useState('');

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Move fetchLabels outside useEffect
  const fetchLabels = async () => {
    try {
      const res = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/labels', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const data = await res.json();
      console.log('📬 All Labels:', data.labels);

      const desiredLabels = ["IN PROGRESS *GB*", "DONE *GB*"];
      const existingLabels = data.labels || [];

      for (const labelName of desiredLabels) {
        const existing = existingLabels.find(l => l.name === labelName);
        if (existing) {
          localStorage.setItem(labelName, existing.id);
        } else {
          try {
            const createRes = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/labels', {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                name: labelName,
                labelListVisibility: 'labelShow',
                messageListVisibility: 'show',
              }),
            });

            if (createRes.ok) {
              const newLabel = await createRes.json();
              localStorage.setItem(labelName, newLabel.id);
              console.log(`✅ Created label ${labelName}:`, newLabel.id);
            } else {
              const err = await createRes.json();
              console.error(`❌ Failed to create label ${labelName}:`, err);
            }
          } catch (err) {
            console.error(`❌ Error creating label ${labelName}:`, err);
          }
        }
      }

      const inProgressLabelId = localStorage.getItem("IN PROGRESS *GB*");
      if (inProgressLabelId) {
        const inProgressRes = await fetch(
          `https://gmail.googleapis.com/gmail/v1/users/me/messages?labelIds=${inProgressLabelId}&maxResults=10`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        const messageList = await inProgressRes.json();
        const detailedMessages = await Promise.all(
          (messageList.messages || []).map(async (msg) => {
            const res = await fetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${msg.id}?format=metadata&metadataHeaders=Subject&metadataHeaders=From`, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            });
            return await res.json();
          })
        );
        setInProgressEmails(detailedMessages);
      }

      const doneLabelId = localStorage.getItem("DONE *GB*");
      if (doneLabelId) {
        const doneRes = await fetch(
          `https://gmail.googleapis.com/gmail/v1/users/me/messages?labelIds=${doneLabelId}&maxResults=10`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        const doneMessageList = await doneRes.json();
        const doneDetailedMessages = await Promise.all(
          (doneMessageList.messages || []).map(async (msg) => {
            const res = await fetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${msg.id}?format=metadata&metadataHeaders=Subject&metadataHeaders=From`, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            });
            return await res.json();
          })
        );
        setDoneEmails(doneDetailedMessages);
      }
    } catch (err) {
      console.error('Failed to fetch labels:', err);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchLabels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const subject = (email) =>
    email?.payload?.headers?.find((h) => h.name === 'Subject')?.value || '';
  const from = (email) =>
    email?.payload?.headers?.find((h) => h.name === 'From')?.value || '';

  const [replyText, setReplyText] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const editorRef = useRef(null);
  const [editorKey, setEditorKey] = useState(0);
  const initialContentRef = useRef('');

  // Check if user can generate AI responses
  const checkGenerationAvailability = async () => {
    if (!userInfo?.email || subscriptionPlan !== 'pro') {
      setCanGenerate(false);
      setGenerationRemaining(0);
      return;
    }

    try {
      const result = await canUserGenerate(userInfo.email, subscriptionPlan, currentPeriodStart);
      setCanGenerate(result.canGenerate);
      setGenerationRemaining(result.remaining);
    } catch (error) {
      console.error('Error checking generation availability:', error);
      setCanGenerate(true); // Default to allowing on error
    }
  };

  // Check generation availability on mount and when relevant props change
  useEffect(() => {
    checkGenerationAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, subscriptionPlan, currentPeriodStart]);

  const handleGenerateResponse = async () => {
    if (!selectedThread || isGenerating) return;

    // Check if user has generation quota
    if (userInfo?.email) {
      const { canGenerate, currentCount, limit, remaining } = await canUserGenerate(userInfo.email, subscriptionPlan, currentPeriodStart);

      if (!canGenerate) {
        alert(`⚠️ Generation Limit Reached\n\nYou've used all ${limit} AI generations for this billing period.\n\nCurrent plan: ${subscriptionPlan || 'Free'}\nGenerations used: ${currentCount}/${limit}\n\nUpgrade your plan to get more generations!`);
        return;
      }

      // Show remaining generations if getting close to limit
      if (remaining <= 5 && remaining > 0) {
        const proceed = window.confirm(`You have ${remaining} AI generation${remaining === 1 ? '' : 's'} remaining this billing period.\n\nDo you want to continue?`);
        if (!proceed) return;
      }
    }

    setIsGenerating(true);
    try {
      const response = await generateAIResponse(selectedThread.messages, knowledgebase, userSettings);

      // Increment generation count after successful generation
      if (userInfo?.email) {
        const result = await incrementGenerationCount(userInfo.email, currentPeriodStart);
        if (result.success) {
          console.log(`✅ Generation tracked. New count: ${result.newCount}`);
          // Refresh generation availability after successful generation
          await checkGenerationAvailability();
        }
      }

      // Convert plain text to HTML with line breaks
      const htmlResponse = response.replace(/\n/g, '<br>');
      setReplyText(htmlResponse);
      initialContentRef.current = htmlResponse;
      setEditorKey(prev => prev + 1); // Force re-render
    } catch (error) {
      alert(`Failed to generate response: ${error.message}`);
      console.error('AI generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const applyFormatting = (format) => {
    const editor = editorRef.current;
    if (!editor) return;

    // Focus the editor first to ensure selection is within it
    editor.focus();

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    if (!range.toString()) return; // No text selected

    switch (format) {
      case 'bold':
        document.execCommand('bold', false, null);
        break;
      case 'italic':
        document.execCommand('italic', false, null);
        break;
      case 'underline':
        document.execCommand('underline', false, null);
        break;
      case 'strikethrough':
        document.execCommand('strikeThrough', false, null);
        break;
      case 'link':
        const url = prompt('Enter URL:', 'https://');
        if (!url) return;
        document.execCommand('createLink', false, url);
        break;
      default:
        return;
    }

    // Update state after formatting
    setReplyText(editor.innerHTML);
  };

const sendReply = async (markAsDone = false) => {
  // Get the actual content from the editor
  const editor = document.getElementById('reply');
  const content = editor?.innerHTML || replyText;

  if (!content || !selectedThread || content === '<span style="color: #9ca3af;">Write your reply here...</span>') return;

  const lastMessage = selectedThread.messages[selectedThread.messages.length - 1];
  const headers = lastMessage.payload?.headers || [];
  const to = headers.find(h => h.name === 'From')?.value || '';
  const subjectLine = headers.find(h => h.name === 'Subject')?.value || '';
  const messageId = headers.find(h => h.name === 'Message-ID')?.value || '';
  const threadId = lastMessage.threadId;

  let rawMessage;

  // Check if content contains HTML tags
  const hasHtml = content.includes('<');
  const emailContent = hasHtml ? content : content.replace(/\n/g, '<br>');

  if (attachments.length === 0) {
    // Simple text message without attachments
    const contentType = hasHtml ? 'text/html' : 'text/plain';
    rawMessage = [
      `To: ${to}`,
      `Subject: Re: ${subjectLine}`,
      `In-Reply-To: ${messageId}`,
      `References: ${messageId}`,
      `Content-Type: ${contentType}; charset="UTF-8"`,
      '',
      emailContent,
    ].join('\n');
  } else {
    // Multipart message with attachments
    const boundary = '----=_Part_' + Date.now();
    const contentType = hasHtml ? 'text/html' : 'text/plain';
    const messageParts = [
      `To: ${to}`,
      `Subject: Re: ${subjectLine}`,
      `In-Reply-To: ${messageId}`,
      `References: ${messageId}`,
      `Content-Type: multipart/mixed; boundary="${boundary}"`,
      '',
      `--${boundary}`,
      `Content-Type: ${contentType}; charset="UTF-8"`,
      '',
      emailContent,
    ];

    // Add each attachment
    for (const file of attachments) {
      const base64Content = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.readAsDataURL(file);
      });

      messageParts.push(
        `--${boundary}`,
        `Content-Type: ${file.type || 'application/octet-stream'}`,
        `Content-Transfer-Encoding: base64`,
        `Content-Disposition: attachment; filename="${file.name}"`,
        '',
        base64Content
      );
    }

    messageParts.push(`--${boundary}--`);
    rawMessage = messageParts.join('\n');
  }

const encodedMessage = btoa(unescape(encodeURIComponent(rawMessage)))
  .replace(/\+/g, '-')
  .replace(/\//g, '_')
  .replace(/=+$/, '');

  try {
    const res = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        raw: encodedMessage,
        threadId,
      }),
    });

    if (res.ok) {
      setReplyText('');
      setAttachments([]);
      initialContentRef.current = '';
      setEditorKey(prev => prev + 1); // Force re-render to clear editor

      // Store the thread for the undo action
      setLastSentThread(selectedThread);

      // Show success toast
      setShowSuccessToast(true);

      // Add "IN PROGRESS *GB*" or "DONE *GB*" label to the first message in the thread
      const labelToAdd = markAsDone
        ? localStorage.getItem("DONE *GB*")
        : localStorage.getItem("IN PROGRESS *GB*");

      const firstMessageId = selectedThread.messages[0]?.id;

      if (labelToAdd && firstMessageId) {
        await fetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${firstMessageId}/modify`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            addLabelIds: [labelToAdd],
            removeLabelIds: [
              'UNREAD',
              ...(markAsDone ? [localStorage.getItem("IN PROGRESS *GB*")] : [])
            ],
          }),
        });
      }
      // Refresh the thread view with updated messages
      const refreshed = await fetch(
        `https://gmail.googleapis.com/gmail/v1/users/me/threads/${threadId}?format=full`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      const updatedThread = await refreshed.json();
      setSelectedThread(updatedThread);
      fetchLabels();
      // Optionally refetch or remove replied thread from the original "Open" column
      if (selectedThread && selectedThread.messages[0]?.id) {
        const updatedOpenEmails = emails.filter(e => e.threadId !== selectedThread.id);
        setInProgressEmails(prev => [...prev, selectedThread.messages[selectedThread.messages.length - 1]]);
        setEmails(updatedOpenEmails);
        setSelectedThread(null);
      }
    } else {
      const error = await res.json();
      console.error('Failed to send reply:', error);
      alert('❌ Failed to send reply.');
    }
  } catch (error) {
    console.error('Error sending email:', error);
    alert('❌ Network error.');
  }
};

  const handleDragStart = (e, email, column) => {
    setDraggedEmail(email);
    setDraggedFromColumn(column);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDropTarget({ column: null, index: null });
  };

  const handleCardDragOver = (e, column, index) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTarget({ column, index });
  };

  const handleDrop = async (e, targetColumn, targetIndex = null) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedEmail || !draggedFromColumn) {
      setDraggedEmail(null);
      setDraggedFromColumn(null);
      setDropTarget({ column: null, index: null });
      setIsDragging(false);
      return;
    }

    if (draggedFromColumn === targetColumn && targetIndex === null) {
      setDraggedEmail(null);
      setDraggedFromColumn(null);
      setDropTarget({ column: null, index: null });
      setIsDragging(false);
      return;
    }

    let newLabelId = null;
    let removeLabelIds = [];

    if (targetColumn === 'open') {
      removeLabelIds = [
        localStorage.getItem("IN PROGRESS *GB*"),
        localStorage.getItem("DONE *GB*")
      ].filter(Boolean);
    } else if (targetColumn === 'inProgress') {
      newLabelId = localStorage.getItem("IN PROGRESS *GB*");
      removeLabelIds = [
        localStorage.getItem("DONE *GB*")
      ].filter(Boolean);
    } else if (targetColumn === 'done') {
      newLabelId = localStorage.getItem("DONE *GB*");
      removeLabelIds = [
        localStorage.getItem("IN PROGRESS *GB*")
      ].filter(Boolean);
    }

    try {
      const modifyBody = {
        removeLabelIds: removeLabelIds
      };
      
      if (newLabelId) {
        modifyBody.addLabelIds = [newLabelId];
      }

      await fetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${draggedEmail.id}/modify`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(modifyBody),
      });

      // Remove from source column
      if (draggedFromColumn === 'open') {
        setEmails(prev => prev.filter(e => e.id !== draggedEmail.id));
      } else if (draggedFromColumn === 'inProgress') {
        setInProgressEmails(prev => prev.filter(e => e.id !== draggedEmail.id));
      } else if (draggedFromColumn === 'done') {
        setDoneEmails(prev => prev.filter(e => e.id !== draggedEmail.id));
      }

      // Add to target column at specific index
      if (targetColumn === 'open') {
        setEmails(prev => {
          const newEmails = [...prev];
          if (targetIndex !== null) {
            newEmails.splice(targetIndex, 0, draggedEmail);
          } else {
            newEmails.push(draggedEmail);
          }
          return newEmails;
        });
      } else if (targetColumn === 'inProgress') {
        setInProgressEmails(prev => {
          const newEmails = [...prev];
          if (targetIndex !== null) {
            newEmails.splice(targetIndex, 0, draggedEmail);
          } else {
            newEmails.push(draggedEmail);
          }
          return newEmails;
        });
      } else if (targetColumn === 'done') {
        setDoneEmails(prev => {
          const newEmails = [...prev];
          if (targetIndex !== null) {
            newEmails.splice(targetIndex, 0, draggedEmail);
          } else {
            newEmails.push(draggedEmail);
          }
          return newEmails;
        });
      }

      setDraggedEmail(null);
      setDraggedFromColumn(null);
      setDropTarget({ column: null, index: null });
      setIsDragging(false);
    } catch (error) {
      console.error('Failed to move email:', error);
      alert('Failed to move email');
    }
  };

  const handleEmailClick = async (email) => {
    try {
      const response = await fetch(
        `https://gmail.googleapis.com/gmail/v1/users/me/threads/${email.threadId}?format=full`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      const fullThread = await response.json();

      // Check subscription status for the sender
      const senderEmail = from(email);
      console.log('Checking subscription for sender:', senderEmail);

      // Extract email from "Name <email>" format
      const extractEmail = (fromString) => {
        const match = fromString?.match(/<(.+?)>/);
        return match ? match[1] : fromString;
      };

      const customerEmail = extractEmail(senderEmail);

      // Extract email addresses from message content
      const extractEmailsFromContent = (messages) => {
        const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
        const foundEmails = new Set();

        messages.forEach(msg => {
          const body = getEmailBody(msg);
          const matches = body.match(emailRegex);
          if (matches) {
            matches.forEach(email => foundEmails.add(email.toLowerCase()));
          }
        });

        return Array.from(foundEmails);
      };

      const mentionedEmails = extractEmailsFromContent(fullThread.messages);
      console.log('📧 Email addresses found in content:', mentionedEmails);

      if (userInfo?.email) {
        // Check sender's subscription
        if (customerEmail) {
          try {
            console.log('🔍 User email:', userInfo.email);
            console.log('📧 Customer email (extracted):', customerEmail);
            const subscriptionStatus = await checkCustomerDetails(userInfo.email, customerEmail);
            console.log('✅ Full subscription response:', JSON.stringify(subscriptionStatus, null, 2));
            fullThread.subscriptionStatus = subscriptionStatus;
          } catch (error) {
            console.error('❌ Error checking subscription on email open:', error);
          }
        }

        // Check mentioned emails for Stripe accounts
        if (mentionedEmails.length > 0) {
          const mentionedAccounts = [];
          for (const mentionedEmail of mentionedEmails) {
            // Skip if it's the sender's email
            if (mentionedEmail === customerEmail?.toLowerCase()) continue;

            try {
              const status = await checkCustomerDetails(userInfo.email, mentionedEmail);
              if (status.customer) {
                mentionedAccounts.push({
                  email: mentionedEmail,
                  ...status
                });
              }
            } catch (error) {
              console.error(`Error checking ${mentionedEmail}:`, error);
            }
          }
          fullThread.mentionedAccounts = mentionedAccounts;
          console.log('👥 Found Stripe accounts for mentioned emails:', mentionedAccounts.length);
        }
      }

      setSelectedThread(fullThread);
    } catch (error) {
      console.error('Failed to fetch thread:', error);
    }
  };

  const handleCancelSubscription = async (cancelAtPeriodEnd) => {
    if (!cancelSubscriptionId) return;

    try {
      const result = await cancelStripeSubscription(userInfo.email, cancelSubscriptionId, cancelAtPeriodEnd);

      if (result.success) {
        alert(cancelAtPeriodEnd
          ? 'Subscription will be canceled at the end of the billing period.'
          : 'Subscription has been canceled immediately.');

        // Refresh subscription status
        const senderEmail = from(selectedThread.messages[selectedThread.messages.length - 1]);
        const extractEmail = (fromString) => {
          const match = fromString?.match(/<(.+?)>/);
          return match ? match[1] : fromString;
        };
        const customerEmail = extractEmail(senderEmail);

        if (customerEmail) {
          const subscriptionStatus = await checkCustomerDetails(userInfo.email, customerEmail);
          setSelectedThread({ ...selectedThread, subscriptionStatus });
        }
      } else {
        alert('Failed to cancel subscription: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setShowCancelModal(false);
      setCancelSubscriptionId(null);
    }
  };

const getEmailBody = (email) => {
  const walkParts = (parts) => {
    for (const part of parts) {
      if (part.mimeType === 'text/html' && part.body?.data) {
        return { data: part.body.data, isHtml: true };
      }
      if (part.mimeType === 'text/plain' && part.body?.data) {
        return { data: part.body.data, isHtml: false };
      }
      if (part.parts) {
        const found = walkParts(part.parts);
        if (found) return found;
      }
    }
    return null;
  };

  const result = walkParts(email.payload?.parts || []) ||
    (email.payload?.body?.data ? { data: email.payload.body.data, isHtml: false } : null);

  if (!result) return 'No content available.';

  try {
    // Properly decode base64url to UTF-8
    const base64 = result.data.replace(/-/g, '+').replace(/_/g, '/');
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const decoder = new TextDecoder('utf-8');
    let decoded = decoder.decode(bytes);

    // Handle quoted-printable encoding
    decoded = decoded
      .replace(/=\r?\n/g, '') // remove soft line breaks
      .replace(/=([0-9A-F]{2})/g, (match, hex) => String.fromCharCode(parseInt(hex, 16)));

    // Convert plain text to HTML with proper line breaks if not HTML
    if (!result.isHtml) {
      decoded = decoded.replace(/\n/g, '<br>');
    }

    return decoded;
  } catch (e) {
    return 'Failed to decode content.';
  }
};

  const normalizeRefundAmount = (amount) => {
    const parsed = Number(amount);
    if (!Number.isFinite(parsed)) return null;
    return Math.round(parsed * 100);
  };

  const handleRefund = async () => {
    if (!refundPayment) return;

    try {
      let amountInCents = null;
      if (refundAmountType === 'partial') {
        const normalized = normalizeRefundAmount(refundPartialAmount);
        if (!normalized || normalized <= 0) {
          alert('Enter a valid refund amount.');
          return;
        }
        if (normalized > Math.round(refundPayment.amount * 100)) {
          alert('Refund amount exceeds the original payment.');
          return;
        }
        amountInCents = normalized;
      }

      const result = await createStripeRefund(
        userInfo.email,
        refundPayment.id,
        refundPayment.paymentIntentId,
        amountInCents
      );

      if (result.success) {
        alert('Refund created.');
      } else {
        alert('Failed to create refund: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating refund:', error);
      alert('Failed to create refund. Please try again.');
    } finally {
      setShowRefundModal(false);
      setRefundPayment(null);
      setRefundAmountType('full');
      setRefundPartialAmount('');
    }
  };

  return (
    <div className="flex w-full h-full gap-4 px-4 py-4 relative">
      {/* Success Toast */}
      <Toast
        isOpen={showSuccessToast}
        onClose={() => setShowSuccessToast(false)}
        title="Email sent!"
        message='Your reply has been delivered and ticket moved to ✅ "Done"'
        duration={8000}
        icon="success"
        actionButton={
          <button
            onClick={async () => {
              // Move ticket to In Progress instead
              const labelToAdd = localStorage.getItem("IN PROGRESS *GB*");
              const labelToRemove = localStorage.getItem("DONE *GB*");

              if (labelToAdd && lastSentThread) {
                const firstMessageId = lastSentThread.messages[0]?.id;
                if (firstMessageId) {
                  await fetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${firstMessageId}/modify`, {
                    method: 'POST',
                    headers: {
                      Authorization: `Bearer ${accessToken}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      addLabelIds: [labelToAdd],
                      removeLabelIds: [labelToRemove],
                    }),
                  });
                  await fetchLabels();
                }
              }
              setShowSuccessToast(false);
            }}
            className="py-2 px-3 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors font-medium text-left"
          >
            Move to ⏳ "In Progress" instead
          </button>
        }
      />

      {/* Open Column */}
      <div
        className={`bg-gray-100 p-4 flex-1 rounded-lg shadow h-full overflow-y-auto transition-all ${
          isDragging && dropTarget.column === 'open' && dropTarget.index === null ? 'bg-gray-200' : ''
        }`}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, 'open')}
      >
        <h3 className="text-xl font-bold mb-4 text-gray-700">📥 Open</h3>

        {emails.map((email, index) => (
          <Fragment key={email.id}>
            {isDragging && dropTarget.column === 'open' && dropTarget.index === index && (
              <div className="h-2 bg-blue-400 rounded mb-3 animate-pulse"></div>
            )}
            <div
              onClick={() => handleEmailClick(email)}
              className={`cursor-pointer transition-opacity ${
                isDragging && draggedEmail?.id === email.id ? 'opacity-30' : ''
              }`}
              draggable
              onDragStart={(e) => handleDragStart(e, email, 'open')}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleCardDragOver(e, 'open', index)}
              onDrop={(e) => handleDrop(e, 'open', index)}
            >
              <EmailCard
                subject={subject(email)}
                from={from(email)}
                userEmail={userInfo?.email}
                onMarkAsDone={async () => {
                  const doneLabel = localStorage.getItem("DONE *GB*");
                  const progressLabel = localStorage.getItem("IN PROGRESS *GB*");
                  if (!doneLabel) return;
                  await fetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${email.id}/modify`, {
                    method: 'POST',
                    headers: {
                      Authorization: `Bearer ${accessToken}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      addLabelIds: [doneLabel],
                      removeLabelIds: ['UNREAD', progressLabel].filter(Boolean),
                    }),
                  });
                  setEmails((prev) => prev.filter((e) => e.id !== email.id));
                  fetchLabels();
                }}
              />
            </div>
          </Fragment>
        ))}
        {isDragging && dropTarget.column === 'open' && dropTarget.index === emails.length && (
          <div className="h-2 bg-blue-400 rounded mb-3 animate-pulse"></div>
        )}
      </div>

      <div
        className={`bg-yellow-100 p-4 flex-1 rounded-lg shadow h-full overflow-y-auto transition-all ${
          isDragging && dropTarget.column === 'inProgress' && dropTarget.index === null ? 'bg-yellow-200' : ''
        }`}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, 'inProgress')}
      >
        <h3 className="text-xl font-bold mb-4 text-yellow-800">⏳ In Progress</h3>

        {inProgressEmails.map((email, index) => (
          <Fragment key={email.id}>
            {isDragging && dropTarget.column === 'inProgress' && dropTarget.index === index && (
              <div className="h-2 bg-blue-400 rounded mb-3 animate-pulse"></div>
            )}
            <div
              onClick={() => handleEmailClick(email)}
              className={`cursor-pointer transition-opacity ${
                isDragging && draggedEmail?.id === email.id ? 'opacity-30' : ''
              }`}
              draggable
              onDragStart={(e) => handleDragStart(e, email, 'inProgress')}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleCardDragOver(e, 'inProgress', index)}
              onDrop={(e) => handleDrop(e, 'inProgress', index)}
            >
              <EmailCard
                subject={subject(email)}
                from={from(email)}
                userEmail={userInfo?.email}
                onMarkAsDone={async () => {
                  const doneLabel = localStorage.getItem("DONE *GB*");
                  if (!doneLabel) return;
                  await fetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${email.id}/modify`, {
                    method: 'POST',
                    headers: {
                      Authorization: `Bearer ${accessToken}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      addLabelIds: [doneLabel],
                      removeLabelIds: [localStorage.getItem("IN PROGRESS *GB*")],
                    }),
                  });
                  // Refresh emails after marking as done
                  fetchLabels();
                }}
              />
            </div>
          </Fragment>
        ))}
        {isDragging && dropTarget.column === 'inProgress' && dropTarget.index === inProgressEmails.length && (
          <div className="h-2 bg-blue-400 rounded mb-3 animate-pulse"></div>
        )}
      </div>

      <div
        className={`bg-green-100 p-4 flex-1 rounded-lg shadow h-full overflow-y-auto transition-all ${
          isDragging && dropTarget.column === 'done' && dropTarget.index === null ? 'bg-green-200' : ''
        }`}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, 'done')}
      >
        <h3 className="text-xl font-bold mb-4 text-green-800">✅ Done</h3>

        {doneEmails.map((email, index) => (
          <Fragment key={email.id}>
            {isDragging && dropTarget.column === 'done' && dropTarget.index === index && (
              <div className="h-2 bg-blue-400 rounded mb-3 animate-pulse"></div>
            )}
            <div
              onClick={() => handleEmailClick(email)}
              className={`cursor-pointer transition-opacity ${
                isDragging && draggedEmail?.id === email.id ? 'opacity-30' : ''
              }`}
              draggable
              onDragStart={(e) => handleDragStart(e, email, 'done')}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleCardDragOver(e, 'done', index)}
              onDrop={(e) => handleDrop(e, 'done', index)}
            >
              <EmailCard
                subject={subject(email)}
                from={from(email)}
                userEmail={userInfo?.email}
                onReopen={async () => {
                  const doneLabel = localStorage.getItem("DONE *GB*");
                  const progressLabel = localStorage.getItem("IN PROGRESS *GB*");
                  if (!doneLabel || !progressLabel) return;
                  await fetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${email.id}/modify`, {
                    method: 'POST',
                    headers: {
                      Authorization: `Bearer ${accessToken}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      addLabelIds: [progressLabel],
                      removeLabelIds: [doneLabel],
                    }),
                  });
                  fetchLabels(); // Refresh UI
                }}
              />
            </div>
          </Fragment>
        ))}
        {isDragging && dropTarget.column === 'done' && dropTarget.index === doneEmails.length && (
          <div className="h-2 bg-blue-400 rounded mb-3 animate-pulse"></div>
        )}
      </div>

      {/* Full Screen Modal */}
      {selectedThread && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full h-full bg-white shadow-2xl rounded-lg flex flex-col">
            <div className="flex justify-between items-start px-6 pt-6 pb-2 border-b border-gray-200">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-800 mb-1">
                {subject(selectedThread.messages[selectedThread.messages.length - 1]) || 'No Subject'}
              </h2>
              <p className="text-sm text-gray-500">
                From: {from(selectedThread.messages[selectedThread.messages.length - 1])}
              </p>
            </div>
            <button
              onClick={() => setSelectedThread(null)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all shadow-sm hover:shadow-md border border-gray-200 hover:border-gray-300"
              title="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            </div>
            <div className="flex-1 flex overflow-hidden">
              {/* Left Column - Messages (2/3) */}
              <div className="w-2/3 flex flex-col border-r border-gray-200">
                <div className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50">
              {selectedThread.messages.map((msg) => {
                const senderEmail = from(msg);
                const isCurrentUser = userInfo?.email && senderEmail.toLowerCase().includes(userInfo.email.toLowerCase());

                return (
                  <div key={msg.id} className={`mb-4 flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`flex items-start gap-3 ${isCurrentUser ? 'flex-row-reverse' : ''} max-w-[85%]`}>
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full ${isCurrentUser ? 'bg-green-500' : 'bg-blue-500'} flex items-center justify-center text-white text-sm font-semibold`}>
                        {senderEmail.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`flex items-baseline gap-2 mb-1 ${isCurrentUser ? 'justify-end' : ''}`}>
                          <span className="font-semibold text-gray-900 text-sm">{isCurrentUser ? 'You' : senderEmail}</span>
                          <span className="text-xs text-gray-500">
                            {new Date(parseInt(msg.internalDate)).toLocaleString()}
                          </span>
                        </div>
                        <div className={`${isCurrentUser ? 'bg-green-100 border-green-200' : 'bg-white border-gray-200'} rounded-lg p-3 shadow-sm border`}>
                          <div
                            className="prose prose-sm max-w-none text-gray-800 [&_*]:text-left"
                            dangerouslySetInnerHTML={{ __html: getEmailBody(msg) }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-2 pb-3 pt-3 border-t border-gray-200">
            <div className="flex flex-col">
              {/* Generate Response Button */}
              <div className="mb-2">
                {subscriptionPlan === 'basic' ? (
                  <div className="bg-gray-100 border border-gray-300 text-gray-600 text-sm px-4 py-2 rounded-lg flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span>AI generation requires Pro plan</span>
                  </div>
                ) : !canGenerate ? (
                  <div className="bg-red-50 border border-red-300 text-red-700 text-sm px-4 py-2 rounded-lg flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>Generation limit reached for this billing period</span>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={handleGenerateResponse}
                      disabled={isGenerating || subscriptionPlan !== 'pro' || !canGenerate}
                      className="bg-purple-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-purple-700 transition-all shadow-sm hover:shadow-md flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isGenerating ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Generating...
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          Generate AI Response
                        </>
                      )}
                    </button>
                    {generationRemaining !== null && generationRemaining <= 10 && generationRemaining > 0 && (
                      <span className="text-xs text-gray-500 px-1">
                        {generationRemaining} generation{generationRemaining === 1 ? '' : 's'} remaining
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div
                key={editorKey}
                ref={(el) => {
                  editorRef.current = el;
                  if (el && initialContentRef.current) {
                    el.innerHTML = initialContentRef.current;
                    initialContentRef.current = '';
                  }
                }}
                id="reply"
                contentEditable={!isGenerating}
                suppressContentEditableWarning
                className="w-full p-3 border border-gray-300 rounded-t-md border-b-0 shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px] max-h-[240px] overflow-y-auto empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800"
                data-placeholder="Write your reply here..."
                onInput={(e) => {
                  setReplyText(e.currentTarget.innerHTML);
                }}
                onClick={(e) => {
                  // Handle link clicks to show edit tooltip
                  if (e.target.tagName === 'A') {
                    e.preventDefault();
                    const link = e.target;
                    const url = link.href;

                    // Create edit tooltip
                    const existingTooltip = document.getElementById('link-edit-tooltip');
                    if (existingTooltip) existingTooltip.remove();

                    const tooltip = document.createElement('div');
                    tooltip.id = 'link-edit-tooltip';
                    tooltip.className = 'absolute z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-2 flex items-center gap-2';
                    tooltip.style.top = `${e.clientY + 5}px`;
                    tooltip.style.left = `${e.clientX}px`;

                    const linkText = document.createElement('a');
                    linkText.href = url;
                    linkText.target = '_blank';
                    linkText.rel = 'noopener noreferrer';
                    linkText.className = 'text-blue-600 hover:underline text-sm max-w-[200px] truncate';
                    linkText.textContent = url;

                    const editBtn = document.createElement('button');
                    editBtn.className = 'text-gray-600 hover:text-gray-900 p-1';
                    editBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>';
                    editBtn.title = 'Edit link';
                    editBtn.onclick = () => {
                      const newUrl = prompt('Edit URL:', url);
                      if (newUrl && newUrl !== url) {
                        link.href = newUrl;
                        setReplyText(editorRef.current.innerHTML);
                      }
                      tooltip.remove();
                    };

                    const removeBtn = document.createElement('button');
                    removeBtn.className = 'text-red-600 hover:text-red-800 p-1';
                    removeBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>';
                    removeBtn.title = 'Remove link';
                    removeBtn.onclick = () => {
                      const textNode = document.createTextNode(link.textContent);
                      link.parentNode.replaceChild(textNode, link);
                      setReplyText(editorRef.current.innerHTML);
                      tooltip.remove();
                    };

                    tooltip.appendChild(linkText);
                    tooltip.appendChild(editBtn);
                    tooltip.appendChild(removeBtn);
                    document.body.appendChild(tooltip);

                    // Close tooltip when clicking outside
                    const closeTooltip = (event) => {
                      if (!tooltip.contains(event.target) && event.target !== link) {
                        tooltip.remove();
                        document.removeEventListener('click', closeTooltip);
                      }
                    };
                    setTimeout(() => document.addEventListener('click', closeTooltip), 0);
                  }
                }}
                style={{ whiteSpace: 'pre-wrap' }}
              />
              {/* Attachments display */}
              {attachments.length > 0 && (
                <div className="px-3 py-2 border-x border-gray-300 flex flex-wrap gap-2 bg-gray-50">
                  {attachments.map((file, index) => (
                    <div key={index} className="text-xs bg-white px-2 py-1 rounded flex items-center gap-1 border border-gray-200">
                      <span>{file.name}</span>
                      <button
                        onClick={() => setAttachments(prev => prev.filter((_, i) => i !== index))}
                        className="text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {/* Combined toolbar with formatting, attachment, and send buttons */}
              <div className="flex items-center justify-between gap-3 p-2 bg-gray-50 rounded-b-lg border border-gray-300 border-t-0">
              <div className="flex gap-1">
                {/* Formatting buttons */}
                <button
                  onClick={() => applyFormatting('bold')}
                  className="px-3 py-1.5 text-sm font-bold border border-gray-300 rounded-md hover:bg-white hover:border-gray-400 hover:shadow-sm transition-all"
                  title="Bold"
                >
                  B
                </button>
                <button
                  onClick={() => applyFormatting('italic')}
                  className="px-3 py-1.5 text-sm italic border border-gray-300 rounded-md hover:bg-white hover:border-gray-400 hover:shadow-sm transition-all"
                  title="Italic"
                >
                  I
                </button>
                <button
                  onClick={() => applyFormatting('underline')}
                  className="px-3 py-1.5 text-sm underline border border-gray-300 rounded-md hover:bg-white hover:border-gray-400 hover:shadow-sm transition-all"
                  title="Underline"
                >
                  U
                </button>
                <button
                  onClick={() => applyFormatting('strikethrough')}
                  className="px-3 py-1.5 text-sm line-through border border-gray-300 rounded-md hover:bg-white hover:border-gray-400 hover:shadow-sm transition-all"
                  title="Strikethrough"
                >
                  S
                </button>
                <button
                  onClick={() => applyFormatting('link')}
                  className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-white hover:border-gray-400 hover:shadow-sm transition-all flex items-center"
                  title="Insert Link"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </button>

                <div className="w-px h-6 bg-gray-300 mx-1"></div>

                {/* Attachment button */}
                <label className="cursor-pointer text-sm text-gray-700 hover:text-gray-900 flex items-center gap-1 border border-gray-300 px-2.5 py-1.5 rounded-md hover:bg-white hover:border-gray-400 hover:shadow-sm transition-all">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setAttachments(prev => [...prev, ...files]);
                    }}
                  />
                </label>
              </div>

              {/* Send buttons */}
              <div className="flex gap-2">
                <button
                  onClick={sendReply}
                  className="bg-blue-600 text-white text-sm px-5 py-2 rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow-md flex items-center gap-2 font-medium"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                  </svg>
                  Send
                </button>
              </div>
              </div>
            </div>
                </div>
              </div>
              {/* Right Column (1/3) */}
              <div className="w-1/3 flex flex-col p-6 overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Customer Info</h3>

                {/* Subscription Info */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700">Sender's Subscription</h4>
                    {selectedThread.subscriptionStatus?.customer?.id ? (
                      <a
                        href={`https://dashboard.stripe.com/customers/${selectedThread.subscriptionStatus.customer.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                      >
                        View in Stripe
                      </a>
                    ) : null}
                  </div>
                  {selectedThread.subscriptionStatus?.hasSubscription && selectedThread.subscriptionStatus.subscriptions?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedThread.subscriptionStatus.subscriptions.map((sub, idx) => (
                        <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="font-semibold text-green-800">Active Subscription</span>
                            {sub.cancelAtPeriodEnd ? (
                              <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">
                                Cancelled
                              </span>
                            ) : null}
                          </div>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Plan:</span>
                              <span className="font-medium text-gray-900">{sub.plan}</span>
                            </div>
                            {sub.product ? (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Product:</span>
                                <span className="font-medium text-gray-900">{sub.product}</span>
                              </div>
                            ) : null}
                            <div className="flex justify-between">
                              <span className="text-gray-600">Amount:</span>
                              <span className="font-medium text-gray-900">{sub.currency} {sub.amount}</span>
                            </div>
                            {sub.status === 'trialing' && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Status:</span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Trial</span>
                              </div>
                            )}
                            {sub.currentPeriodEnd ? (
                              <div className="flex justify-between">
                                <span className="text-gray-600">
                                  {sub.cancelAtPeriodEnd ? 'Ends on:' : 'Renews on:'}
                                </span>
                                <span className="font-medium text-gray-900">
                                  {formatDateTime(sub.currentPeriodEnd)}
                                </span>
                              </div>
                            ) : null}
                          </div>
                          {!sub.cancelAtPeriodEnd && (
                            <button
                              onClick={() => {
                                setCancelSubscriptionId(sub.id);
                                setShowCancelModal(true);
                              }}
                              className="mt-3 w-full bg-red-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                            >
                              Cancel Subscription
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <p className="text-sm text-gray-600">No active subscription</p>
                    </div>
                  )}
                </div>

                {/* Payment History */}
                {selectedThread.subscriptionStatus?.payments && selectedThread.subscriptionStatus.payments.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Recent Payments</h4>
                    <div className="space-y-2">
                      {selectedThread.subscriptionStatus.payments.slice(0, 3).map((payment) => (
                        <div key={payment.id} className="bg-white border border-gray-200 rounded-lg p-3">
                          <div className="flex justify-between items-start mb-1">
                            <span className="text-sm font-medium text-gray-900">
                              {payment.currency} {payment.amount}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              payment.status === 'succeeded' ? 'bg-green-100 text-green-800' :
                              payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {payment.status}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDateTime(payment.created)}
                          </div>
                          {payment.description && payment.description !== 'Payment' && (
                            <div className="text-xs text-gray-600 mt-1">{payment.description}</div>
                          )}
                          <div className="mt-3 flex justify-end">
                            <button
                              type="button"
                              onClick={() => {
                                setRefundPayment(payment);
                                setRefundAmountType('full');
                                setRefundPartialAmount('');
                                setShowRefundModal(true);
                              }}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700"
                            >
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 3l4 4-4 4M20 7H9a6 6 0 00-6 6v4" />
                              </svg>
                              Refund
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mentioned Email Accounts */}
                {selectedThread.mentionedAccounts && selectedThread.mentionedAccounts.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Mentioned Customers</h4>
                    <div className="space-y-3">
                      {selectedThread.mentionedAccounts.map((account, idx) => (
                        <div key={idx} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="text-sm font-medium text-gray-900 mb-2">{account.email}</div>
                          {account.hasSubscription && account.subscriptions?.length > 0 ? (
                            <div className="space-y-1">
                              {account.subscriptions.map((sub, subIdx) => (
                                <div key={subIdx} className="text-xs">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Plan:</span>
                                    <span className="font-medium text-gray-900">{sub.plan}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Amount:</span>
                                    <span className="font-medium text-gray-900">{sub.currency} {sub.amount}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-600">Customer exists but no active subscription</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cancel Subscription</h3>
            <p className="text-sm text-gray-600 mb-6">
              How would you like to cancel this subscription?
            </p>
            <div className="space-y-3">
              <button
                onClick={() => handleCancelSubscription(true)}
                className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors text-left"
              >
                <div className="font-medium">Cancel at period end</div>
                <div className="text-sm text-blue-100">Customer keeps access until billing period ends</div>
              </button>
              <button
                onClick={() => handleCancelSubscription(false)}
                className="w-full bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 transition-colors text-left"
              >
                <div className="font-medium">Cancel immediately</div>
                <div className="text-sm text-red-100">Customer loses access right away</div>
              </button>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelSubscriptionId(null);
                }}
                className="w-full bg-gray-200 text-gray-800 px-4 py-3 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Don't cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && refundPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Issue refund</h3>
            <p className="text-sm text-gray-600 mb-4">
              Choose full or partial refund for {refundPayment.currency} {refundPayment.amount}.
            </p>

            <div className="space-y-3 mb-4">
              <button
                onClick={() => setRefundAmountType('full')}
                className={`w-full px-4 py-3 rounded-lg text-left transition-colors ${
                  refundAmountType === 'full'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                <div className="font-medium">Full refund</div>
                <div className="text-sm opacity-80">Refund the entire payment</div>
              </button>
              <button
                onClick={() => setRefundAmountType('partial')}
                className={`w-full px-4 py-3 rounded-lg text-left transition-colors ${
                  refundAmountType === 'partial'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                <div className="font-medium">Partial refund</div>
                <div className="text-sm opacity-80">Choose the amount to refund</div>
              </button>
            </div>

            {refundAmountType === 'partial' ? (
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700">Refund amount</label>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-sm text-gray-500">{refundPayment.currency}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={refundPartialAmount}
                    onChange={(event) => setRefundPartialAmount(event.target.value)}
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="0.00"
                  />
                </div>
              </div>
            ) : null}

            <div className="flex gap-2">
              <button
                onClick={handleRefund}
                className="flex-1 bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 transition-colors"
              >
                Issue refund
              </button>
              <button
                onClick={() => {
                  setShowRefundModal(false);
                  setRefundPayment(null);
                  setRefundAmountType('full');
                  setRefundPartialAmount('');
                }}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-3 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Board;
